import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import OneStaff from "./OneStaff";
import { FaGraduationCap, FaArrowRight } from "react-icons/fa6";
import { IoMdArrowDropdown } from "react-icons/io";

/* ------------------------------------------------------------------ */
/* Scroll-reveal utility                                              */
/* ------------------------------------------------------------------ */
function useReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px", ...options }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [ref, visible];
}

function Reveal({ as: Tag = "div", children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* Main Component                                                     */
/* ------------------------------------------------------------------ */
export default function StaffBanner() {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  async function init() {
    setReady(false);
    await appContext.getAllStaff();
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);


  if (!ready) {
    return (
      <div className="container py-5 d-flex justify-content-center" style={{ backgroundColor: '#0f172a', minHeight: '50vh', alignItems: 'center' }}>
        {getInlineLoader()}
      </div>
    );
  }

  const keyStaff = Array.isArray(appContext.allStaff) ? appContext.allStaff : [];
  
  // Routing Logic Check
  const isStaffRoute = appContext.item === 'staff';
  const displayStaff = isStaffRoute ? keyStaff : keyStaff.slice(0, 3);

  return (
    <section className="mod-staff-section">
      <style>{`
        /* --- Base & Dark Theme --- */
        .mod-staff-section {
          background-color: #0f172a; /* Deep Navy Slate */
          color: #f8fafc;
          font-family: system-ui, -apple-system, sans-serif;
          padding: 6rem 0;
          position: relative;
        }

        /* --- Scroll Reveal Animations --- */
        .reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
        }
        .reveal.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* --- Typography --- */
        .mod-kicker {
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #60a5fa;
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: block;
        }
        .mod-title {
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 3rem;
          line-height: 1.2;
        }

        /* --- Cards & Glassmorphism --- */
        .mod-card {
          background: rgba(30, 41, 59, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 2.5rem;
          backdrop-filter: blur(10px);
          transition: transform 0.3s ease, border-color 0.3s ease;
          display: flex;
          flex-direction: column;
        }
        .mod-card:hover {
          transform: translateY(-5px);
          border-color: rgba(96, 165, 250, 0.3);
        }

        /* --- Partner Specific Styles --- */
        .partner-avatar-wrap {
          width: 100%;
          height: auto;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.1);
          background: #1e293b;
        }
        .partner-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          filter: contrast(1.1);
        }
        .mod-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(59, 130, 246, 0.1);
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.2);
          padding: 0.4rem 0.8rem;
          border-radius: 50px;
          font-size: 0.8rem;
          font-weight: 500;
          margin-top: 0.5rem;
        }
        .mod-list {
          list-style: none;
          padding: 0;
          margin: 1.5rem 0;
          flex-grow: 1;
        }
        .mod-list li {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #94a3b8;
          margin-bottom: 0.75rem;
          font-size: 0.95rem;
        }
        .mod-list-dot {
          width: 6px;
          height: 6px;
          background: #3b82f6;
          border-radius: 50%;
          box-shadow: 0 0 8px rgba(59,130,246,0.6);
        }

        /* --- Buttons --- */
        .mod-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 24px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          border: none;
        }
        .mod-btn-primary {
          background-color: #2563eb;
          color: white;
          box-shadow: 0 4px 14px 0 rgba(37, 99, 235, 0.39);
        }
        .mod-btn-primary:hover {
          background-color: #1d4ed8;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(37, 99, 235, 0.5);
        }
        .mod-btn-outline {
          background-color: transparent;
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(5px);
        }
        .mod-btn-outline:hover {
          background-color: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.8);
          transform: translateY(-2px);
        }

        /* --- Dynamic Staff Overrides for dark theme --- */
        .staff-dynamic-wrap {
          height: 100%;
          transition: transform 0.3s ease;
        }
        .staff-dynamic-wrap:hover {
          transform: translateY(-4px);
        }
        /* Assuming OneStaff defaults to light colors, we inject a wrapper filter or adjust it natively if possible. 
           If OneStaff uses standard Bootstrap cards, we can force dark mode in this container */
        .staff-dynamic-wrap .card {
          background: rgba(30, 41, 59, 0.5) !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
          color: #f8fafc !important;
        }
        .staff-dynamic-wrap .text-dark, .staff-dynamic-wrap .text-muted {
          color: #94a3b8 !important;
        }
        .mod-empty-state {
          background: rgba(30, 41, 59, 0.3);
          border: 1px dashed rgba(255,255,255,0.1);
          border-radius: 16px;
          padding: 3rem;
          color: #64748b;
        }
      `}</style>

      <div className="container">
        

        {/* --- SECTION 2: KEY STAFF (DYNAMIC) --- */}
        <div className="text-center mb-5 mt-5">
          <Reveal>
            <h2 className="mod-title mb-2" style={{ fontSize: '2rem' }}>Our Team</h2>
            <p style={{ color: '#94a3b8' }}>Specialized staff supporting our global practice areas</p>
          </Reveal>
        </div>

        <div className="row g-4 justify-content-center">
          {displayStaff.length > 0 ? (
            displayStaff.map((item, i) => (
              <div key={i} className="col-12 col-md-6 col-lg-4">
                <Reveal delay={i * 100} className="staff-dynamic-wrap">
                   <OneStaff data={item} />
                </Reveal>
              </div>
            ))
          ) : (
            <div className="col-12">
              <Reveal className="mod-empty-state text-center">
                <p className="mb-0 fst-italic">No additional personnel records found.</p>
              </Reveal>
            </div>
          )}
        </div>

        {/* --- CONDITIONAL CTA: VIEW ALL STAFF --- */}
        {!isStaffRoute && keyStaff.length > 3 && (
          <Reveal delay={200}>
            <div className="text-center mt-5 pt-3">
              <button
                className="mod-btn mod-btn-outline"
                onClick={() => appContext.navTo({ item: "staff" })}
              >
                View All Staff <FaArrowRight />
              </button>
            </div>
          </Reveal>
        )}

      </div>
    </section>
  );
}